import { TypedDocumentNode, gql } from '@apollo/client';
import { Fragment } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { StatCategory } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { Stats } from '@sorare/core/src/lib/scoring';

import { isCategoryDecisive, statToLabel, statToValue } from '../../utils';
import type {
  CategoryStat_playerGameScoreInterface,
  CategoryStat_statScore,
} from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  justify-content: space-between;
`;
const Stat = styled(BodyS)`
  flex: 1;
  &::first-letter {
    text-transform: uppercase;
  }
`;
const Score = styled(BodyS)`
  min-width: var(--quadruple-unit);
  font-variant-numeric: tabular-nums;
  text-align: right;
`;
const Value = styled(LabelS)`
  font-variant-numeric: tabular-nums;
  text-align: right;
`;

type Props = {
  category: StatCategory;
  stat: CategoryStat_statScore;
  playerGameScores: CategoryStat_playerGameScoreInterface[];
};

export const CategoryStat = ({ category, stat, playerGameScores }: Props) => {
  const { formatNumber } = useIntlContext();
  const isDecisive = isCategoryDecisive(category);

  const statLabel = statToLabel(stat.stat);

  return (
    <Root>
      {!!isDecisive && <StatIcon statKey={stat.stat as Stats} />}
      <Stat as="p" color="var(--c-nd-600)">
        {typeof statLabel === 'string' ? (
          statLabel
        ) : (
          <FormattedMessage {...statLabel} />
        )}
      </Stat>
      {playerGameScores.map(playerGameScore => {
        const statScore = playerGameScore.detailedScore.find(
          score => score.stat === stat.stat
        );

        const scoreString = isDecisive
          ? statScore?.statValue
          : statScore?.totalScore;

        const statValue = statToValue(stat.stat, statScore?.statValue);

        return (
          <Fragment key={playerGameScore.id}>
            {!isDecisive && !!statScore?.statValue && (
              <Value as="p" color="var(--c-nd-400)">
                (
                {typeof statValue === 'string' ? (
                  statValue
                ) : (
                  <FormattedNumber value={statScore.statValue} />
                )}
                )
              </Value>
            )}
            <Score as="p" color="var(--c-nd-600)">
              {scoreString ? formatNumber(scoreString) : '-'}
            </Score>
          </Fragment>
        );
      })}
    </Root>
  );
};

CategoryStat.fragments = {
  statScore: gql`
    fragment CategoryStat_statScore on StatScore {
      stat
    }
  ` as TypedDocumentNode<CategoryStat_statScore>,
  playerGameScoreInterface: gql`
    fragment CategoryStat_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      detailedScore {
        stat
        statValue
        totalScore
      }
    }
  ` as TypedDocumentNode<CategoryStat_playerGameScoreInterface>,
};
